
import _ from 'underscore'
import d3 from 'd3'
import Backbone from 'backbone'
import LevelView from './LevelView'

class MapView extends Backbone.View {

  initialize() {
    this.zoom = d3.behavior.zoom()
    this.bindEvents()
  }

  bindEvents() {
    this.bndHandleZoom = this.handleZoom.bind(this)
    this.listenTo(this.model, 'change:x change:y change:scale change:scaleExtent', this.handleTransform)
    this.listenTo(this.model, 'change:viewportWidth change:viewportHeight change:zoomEnabled', this.handleResize)
    this.listenTo(this.model, 'change:panZoom', this.renderPanZoom)
    this.listenTo(this.model, 'change:transition', this.renderTransition)

    let levels = this.model.get('levels')
    this.listenTo(levels, 'add', this.handleLevelAddition)
    this.listenTo(levels, 'remove', this.handleLevelRemoval)

    this.zoom.on('zoom', this.bndHandleZoom)    
  }

  unbindEvents() {
    this.stopListening()
    this.zoom.off('zoom', this.bndHandleZoom)
  }

  handleLevelAddition(model, collection, options) {
    let levelView = new LevelView({
      model: model,
      el: this.map.insert('g', `#map > g:nth-child(${options.index + 1})`)
        .attr('id', model.cid)
        .node()
    })

    levelView.render()
  }

  handleLevelRemoval(model, collection, options) {
    this.map.select(`#${model.cid}`).remove()
  }

  handleZoom() {
    this.model.set({
      x: d3.event.translate[0],
      y: d3.event.translate[1],
      scale: d3.event.scale
    })
  }

  handleTransform() {
    let trans = [this.model.get('x'), this.model.get('y')]
    let scale = this.model.get('scale')
    let scaleExtent = this.model.get('scaleExtent')

    this.zoom.translate(trans).scale(scale).scaleExtent(scaleExtent)
    this.renderMapTransform(trans, scale)
  }

  handleResize() {
    let width = this.model.get('viewportWidth')
    let height = this.model.get('viewportHeight')
    this.renderMapSize(width, height)
  }

  clearTransition() {
    if (this.transition) {
      this.map.transition()
      this.transition = null
    }
  }

  renderMapSize(width, height) {
    this.viewport
      .attr('width', width)
      .attr('height', height)
  }

  renderPanZoom() {
    let {viewport, zoom, model} = this
    
    if (model.get('panZoom')) {
      viewport.call(zoom)
    } else {
      viewport.call(zoom)
        .on('mousedown.zoom', null)
        .on('dblclick.zoom', null)
        .on('touchstart.zoom', null)
        .on('touchmove.zoom', null)
        .on('touchend.zoom', null)
        .on('wheel.zoom', null)
        // IE 11 uses mousewheel event:
        .on('mousewheel.zoom', null)
    }
  }

  renderTransition() {
    let {map, model} = this
    let t = model.get('transition')
    
    if (_.isEmpty(t.properties)) {
      return this.clearTransition()
    }

    this.transition = map
      .transition()
      .duration(t.duration)
      .ease(t.ease)

    let p = t.properties

    let x = _.has(p, 'x') ? p.x : model.get('x')
    let y = _.has(p, 'y') ? p.y : model.get('y')
    let scale = _.has(p, 'scale') ? p.scale : model.get('scale')

    this.transition
      .attr('transform', `translate(${x},${y}) scale(${scale})`)

    this.zoom.translate([x, y]).scale(scale)
    model.set({x, y, scale}, {silent: true})
  }

  renderMapTransform(translate, scale) {
    this.map.attr('transform', `translate(${translate}) scale(${scale})`)
  }

  renderBackgroundColor() {
    this.$el.css('background-color', this.model.get('backgroundColor')) 
  }

  render() {
    let {model, el, $el, zoom} = this

    $el.css({
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    })

    zoom.scaleExtent(model.get('scaleExtent'))
    zoom.scale(model.get('scale'))
    zoom.translate([this.model.get('x'), this.model.get('y')])

    let viewport = this.viewport = d3.select(el).append('svg')

    viewport
      .attr('width', model.get('viewportWidth'))
      .attr('height', model.get('viewportHeight'))
      .call(zoom)
        .on('mousedown.zoom', null)
        .on('touchstart.zoom', null)
        .on('touchmove.zoom', null)
        .on('touchend.zoom', null)
        .on('wheel.zoom', null)
        // IE 11 uses mousewheel event:
        .on('mousewheel.zoom', null)

    let map = this.map = viewport.append('g').attr('id', 'map')

    this.renderMapTransform(zoom.translate(), zoom.scale())
    this.renderBackgroundColor()
    this.renderPanZoom()

    // Render each level.
    
    model.get('levels').each(function (level) {      
      let levelView = new LevelView({
        model: level,
        el: map.append('g')
          .attr('id', level.cid)
          .node()
      })
      levelView.render()
    })

    return this
  }
}

export default MapView
